import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { PlayerProfileScopeSwitcher } from "../components/PlayerFamilyProfileSwitcher";
import { makeLatestOnly } from "../lib/latestOnly";
import { usePlayerProfileScope } from "../lib/usePlayerProfileScope";
import {
  api,
  ApiError,
  type MatchResultReport,
  type PlayerReportableMatch,
  type PlayerResultReportsPayload,
} from "../lib/api";
import { routes } from "../routes";
import { formatDate } from "../lib/dateFormat";
import { isSchedulePendingForPlayer, SCHEDULE_PENDING_LABEL } from "../lib/matchScheduleStatus";
import { isPickleballSport } from "../lib/scoreParsing";
import { matchScoreFromWinner } from "../lib/scoreFromWinner";
import ResultSetCapture, { type ResultSetCapturePayload } from "../components/ResultSetCapture";

const STATUS_LABELS: Record<string, string> = {
  pending: "Pendiente de aprobación",
  approved: "Aprobado",
  rejected: "Rechazado",
  superseded: "Reemplazado",
};


function describeMatchTitle(match: PlayerReportableMatch): string {
  const a = match.match.player1?.display_name ?? "Por definir";
  const b = match.match.player2?.display_name ?? "Por definir";
  return `${a} vs ${b}`;
}

/** Marcador de un reporte desde quien ganó (Eduardo 2026-10-06). */
function reportScoreFromWinner(report: MatchResultReport): string {
  return matchScoreFromWinner({
    score_text: report.score_text,
    winner_participant_id: report.winner_participant_id,
    player1: report.match?.player1 ?? null,
    player2: report.match?.player2 ?? null,
  });
}

function reportStatusBadge(status: string) {
  const label = STATUS_LABELS[status] ?? status;
  const className =
    status === "pending"
      ? "kbd kbd--warning"
      : status === "approved"
        ? "kbd kbd--ok"
        : status === "rejected"
          ? "kbd kbd--danger"
          : "kbd";
  return <span className={className}>{label}</span>;
}

function ReportRow({ report }: { report: MatchResultReport }) {
  const opponent = report.match
    ? report.reported_by_participant_id === report.match.player1?.id
      ? report.match.player2
      : report.match.player1
    : null;
  return (
    <tr>
      <td>{report.tournament_name ?? "—"}</td>
      <td>
        {isSchedulePendingForPlayer(report.match) ? SCHEDULE_PENDING_LABEL : formatDate(report.match?.scheduled_date)}
        {report.match?.scheduled_time
          ? ` ${report.match.scheduled_time}`
          : ""}
      </td>
      <td>{opponent?.display_name ?? "—"}</td>
      <td>{reportScoreFromWinner(report) || "—"}</td>
      <td>{report.winner?.display_name ?? "—"}</td>
      <td>{reportStatusBadge(report.status)}</td>
      <td>{report.admin_notes ?? "—"}</td>
    </tr>
  );
}

function ReportableMatchCard({
  entry,
  busy,
  highlighted,
  onSubmit,
}: {
  entry: PlayerReportableMatch;
  busy: boolean;
  highlighted: boolean;
  onSubmit: (matchId: number, payload: ResultSetCapturePayload) => Promise<void>;
}) {
  const pickleball = isPickleballSport(entry.settings?.sport);
  const reportingDisabledReason = !entry.can_report
    ? entry.match.status === "completed" || entry.match.score_text || entry.match.winner_participant_id
      ? "Este partido ya tiene resultado publicado. Solo puedes reportar tu enfrentamiento actual."
      : entry.own_participant_id === null
        ? "No participas en este partido."
        : "El reporte por jugador no está habilitado en este torneo."
    : null;
  const p1 = entry.match.player1;
  const p2 = entry.match.player2;
  const roundLabel = entry.match.round_display_label ?? entry.match.round_label;
  const autoApprove = entry.settings?.auto_approve_matching_reports !== false;
  return (
    <article className={highlighted ? "tournament-card is-highlighted" : "tournament-card"}>
      <header className="tournament-card__title">
        {describeMatchTitle(entry)}
      </header>
      <div className="tournament-card__row">
        <span className="muted">Torneo</span>
        <span>
          {entry.match.tournament_name ?? `#${entry.match.tournament_id}`}
          {roundLabel ? ` · ${roundLabel}` : ""}
        </span>
      </div>
      {entry.match.score_text && (
        <div className="tournament-card__row">
          <span className="muted">Score actual</span>
          <span>{matchScoreFromWinner(entry.match)}</span>
        </div>
      )}
      {entry.my_pending_report && (
        <div className="tournament-card__row">
          <span className="muted">Tu reporte</span>
          <span>{reportStatusBadge(entry.my_pending_report.status)}</span>
        </div>
      )}

      {reportingDisabledReason || !p1 || !p2 ? (
        <p className="muted" style={{ margin: "0.5rem 0" }}>
          {reportingDisabledReason ?? "Este partido todavía no tiene a los dos rivales."}
        </p>
      ) : (
        <>
          {/* Captura nueva (maqueta aprobada por Eduardo 2026-10-06): ¿quién
              ganó?, set por set en tarjetas y «¿Así fue?» antes de mandar. El
              texto que se manda es el mismo de siempre. */}
          <ResultSetCapture
            idPrefix={`reporte-${entry.match.id}`}
            player1={{ id: p1.id, name: p1.display_name ?? `Jugador #${p1.id}` }}
            player2={{ id: p2.id, name: p2.display_name ?? `Jugador #${p2.id}` }}
            pickleball={pickleball}
            initialScoreText={entry.my_pending_report?.score_text ?? null}
            initialWinnerId={entry.my_pending_report?.winner_participant_id ?? null}
            initialNotes={entry.my_pending_report?.notes ?? null}
            busy={busy}
            confirmNote={
              autoApprove
                ? "Si tu rival reporta lo mismo, se aprueba solo; si no, lo revisa el organizador. Si algo no cuadra, corrígelo antes de enviar."
                : "Lo verá el organizador para aprobarlo. Si algo no cuadra, corrígelo antes de enviar."
            }
            submitLabel={entry.my_pending_report ? "Revisar y actualizar" : "Revisar y enviar"}
            onSubmit={(payload) => onSubmit(entry.match.id, payload)}
          />
          <div className="form__actions">
            <Link className="btn btn--ghost" to={routes.player}>
              Volver
            </Link>
          </div>
        </>
      )}
    </article>
  );
}

export default function PlayerResultReportsPage() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusMatchId = Number(searchParams.get("match_id") ?? "");
  // Cuenta familiar: «Reportar resultado» desde un torneo trae el perfil con
  // el que se está jugando ese torneo; si no, el perfil activo de la cuenta.
  const linkedProfileId = Number(searchParams.get("player_profile_id") ?? "");
  const scope = usePlayerProfileScope({
    preferredProfileId: Number.isFinite(linkedProfileId) && linkedProfileId > 0 ? linkedProfileId : null,
  });
  const scopeProfileId = scope.profileId;
  const latestLoad = useMemo(() => makeLatestOnly(), []);
  // Datos + el perfil al que pertenecen: al cambiar de hijo (aquí o en otra
  // pestaña) las tarjetas del anterior no se muestran ni se envían con el
  // perfil nuevo (daba 403 «No participas en este partido»).
  const [loaded, setLoaded] = useState<{ profileId: number | null; payload: PlayerResultReportsPayload } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [submittingMatchId, setSubmittingMatchId] = useState<number | null>(
    null,
  );

  const load = useCallback(async () => {
    if (!token || !scope.ready) return;
    const attempt = latestLoad.begin();
    setLoading(true);
    setError(null);
    try {
      const response = await api.resultReports.listForPlayer(token, scopeProfileId);
      if (!latestLoad.isCurrent(attempt)) return;
      setLoaded({ profileId: scopeProfileId, payload: response.data });
    } catch (err) {
      if (!latestLoad.isCurrent(attempt)) return;
      if (err instanceof ApiError) {
        if (err.status === 404) {
          setError(
            "Tu cuenta no está vinculada a un perfil de jugador. Pídele al admin que te genere un PIN.",
          );
        } else {
          setError(err.message || "No pudimos cargar tus partidos.");
        }
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      if (latestLoad.isCurrent(attempt)) setLoading(false);
    }
  }, [latestLoad, scope.ready, scopeProfileId, token]);

  // Cambiar de hijo aquí cambia el perfil activo de la cuenta; el perfil que
  // traía la liga ya no manda.
  const profileSwitcherScope = useMemo(
    () => ({
      ...scope,
      setProfileId: (profileId: number) => {
        scope.setProfileId(profileId);
        setMessage(null);
        if (searchParams.has("player_profile_id")) {
          const next = new URLSearchParams(searchParams);
          next.delete("player_profile_id");
          setSearchParams(next, { replace: true });
        }
      },
    }),
    [scope, searchParams, setSearchParams],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const data = loaded && loaded.profileId === scopeProfileId ? loaded.payload : null;
  const dataProfileId = data ? scopeProfileId : null;

  const handleSubmit = useCallback(
    async (matchId: number, payload: ResultSetCapturePayload) => {
      if (!token || !data) return;
      setMessage(null);
      setError(null);
      setSubmittingMatchId(matchId);
      try {
        const response = await api.resultReports.submitForMatch(token, matchId, payload, dataProfileId);
        // Eduardo 2026-10-06: si el rival ya había reportado lo mismo, el
        // servidor lo aprueba solo.
        setMessage(
          response?.data?.report?.status === "approved"
            ? "Tu rival y tú reportaron lo mismo: el resultado quedó aprobado."
            : "Reporte enviado. Un admin lo revisará pronto.",
        );
        await load();
        // Un matchup (id negativo) se vuelve partido al reportarlo y sale de
        // la lista: la tarjeta sigue en ese partido nuevo en lugar de saltar
        // a otro rival con el formulario vacío.
        const createdMatchId = response?.data?.report?.match_id;
        if (matchId < 0 && typeof createdMatchId === "number" && createdMatchId > 0) {
          setSearchParams(
            (current) => {
              const next = new URLSearchParams(current);
              next.set("match_id", String(createdMatchId));
              return next;
            },
            { replace: true },
          );
        }
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message || "No se pudo enviar el reporte.");
        } else {
          setError("Error de red. Intenta otra vez.");
        }
      } finally {
        setSubmittingMatchId(null);
      }
    },
    [data, dataProfileId, token, load, setSearchParams],
  );

  const currentReportableMatch = useMemo(() => {
    if (!data) return null;
    const matches = data.matches.filter((entry) => entry.can_report);
    if (!matches.length) return null;
    // Un id negativo es un matchup publicado que aún no es partido (el reto
    // de round robin): también se abre ese.
    if (Number.isFinite(focusMatchId) && focusMatchId !== 0) {
      const focused = matches.find((entry) => entry.match.id === focusMatchId);
      if (focused) return focused;
    }
    return [...matches].sort((a, b) => {
      const ad = a.match.scheduled_date ?? "9999-12-31";
      const bd = b.match.scheduled_date ?? "9999-12-31";
      if (ad !== bd) return ad.localeCompare(bd);
      return a.match.id - b.match.id;
    })[0];
  }, [data, focusMatchId]);

  return (
    <>
      <section className="card">
        <div className="card__header-row">
          <div>
            <h1 className="card__title">Reportar resultados</h1>
            <p className="card__subtitle">
              Captura el score de tus partidos. En torneos sin calendario de canchas,
              coordina directo con tu rival y reporta aquí el resultado; el admin
              revisará y aprobará cada reporte antes de actualizar standings y posiciones.
            </p>
          </div>
          <div className="form__actions">
            <Link className="btn btn--ghost" to={routes.player}>
              Home
            </Link>
            <Link className="btn btn--ghost" to={routes.playerProfile}>
              Mi perfil
            </Link>
          </div>
        </div>
        <PlayerProfileScopeSwitcher scope={profileSwitcherScope} />
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        {message && <p className="form-message">{message}</p>}
      </section>

      <section className="card">
        <h2 className="card__title">Enfrentamiento actual</h2>
        {(loading || !scope.ready) && !data ? (
          <p className="muted">Cargando…</p>
        ) : !data || !currentReportableMatch ? (
          <p className="muted">
            No tienes un enfrentamiento actual pendiente de reportar. Los partidos pasados
            con resultado publicado quedan solo como historial.
          </p>
        ) : (
          <div className="tournament-card-grid">
            <ReportableMatchCard
              key={currentReportableMatch.match.id}
              entry={currentReportableMatch}
              highlighted={currentReportableMatch.match.id === focusMatchId}
              busy={submittingMatchId === currentReportableMatch.match.id}
              onSubmit={handleSubmit}
            />
          </div>
        )}
      </section>

      <section className="card">
        <h2 className="card__title">Resultados reportados</h2>
        {data && data.reports.length > 0 ? (
          <div className="responsive-table-wrap">
            <table className="data-table dashboard-table">
              <thead>
                <tr>
                  <th>Torneo</th>
                  <th>Fecha</th>
                  <th>Rival</th>
                  <th>Score</th>
                  <th>Ganador</th>
                  <th>Estado</th>
                  <th>Notas admin</th>
                </tr>
              </thead>
              <tbody>
                {data.reports.map((r) => (
                  <ReportRow key={r.id} report={r} />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted">
            {loading ? "Cargando…" : "Aún no has enviado ningún reporte."}
          </p>
        )}
      </section>
    </>
  );
}
